import { t } from "i18next";

import { Box, Button, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import {
  MileageUnit,
  NoteDTO,
  PreviousAuctionNoteDTO
} from "@/src/types/types";
import { getDateStringDMY } from "@/src/utils/dateUtils";
import { mileageDeltaText } from "@/src/utils/noteUtils";
import { addThousandsSeparator } from "@/src/utils/numberUtils";

import NoteItem from "../../AuctionDetailsPage/NoteItem";

export interface NotesListProps {
  notes?: NoteDTO[];
  historyGroups: PreviousAuctionNoteDTO[][];
  currentMileage?: number | null;
  mileageUnit?: MileageUnit;
  /** "full": modal — deletes, both mileage columns, history-error retry row, empty state.
   *  "preview": hover panel — read-only, "Mittari tuolloin" only, no error row, no empty state. */
  variant?: "full" | "preview";
  isNoteHistoryError?: boolean;
  onRetryHistory?: () => void;
  onDeleteNote?: (note: NoteDTO) => void;
}

const NotesList = (props: NotesListProps) => {
  const {
    notes,
    historyGroups,
    currentMileage,
    mileageUnit,
    variant = "full",
    isNoteHistoryError = false,
    onRetryHistory,
    onDeleteNote
  } = props;
  const theme = useTheme();
  const isPreview = variant === "preview";
  const hasNotes = (notes?.length ?? 0) > 0;
  const hasHistory =
    historyGroups.length > 0 || (!isPreview && isNoteHistoryError);

  const sectionLabelSx = {
    color: theme.palette.text.secondary,
    fontWeight: theme.typography.fontWeightSemibold,
    textTransform: "uppercase" as const,
    letterSpacing: "0.05em",
    mb: 1
  };

  if (isPreview && !hasNotes && historyGroups.length === 0) {
    return null;
  }

  return (
    <>
      {hasNotes && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={sectionLabelSx}>
            {t("dealer.auctionDetailsPage.noteHistory.currentAuction")}
          </Typography>
          {notes!.map((note) => (
            <NoteItem
              onDelete={() => onDeleteNote?.(note)}
              key={`dealer-note-modal-${note.id}`}
              note={note}
              readOnly={isPreview}
            />
          ))}
        </Box>
      )}

      {!isPreview && isNoteHistoryError && (
        <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" color="error">
            {t("dealer.auctionDetailsPage.noteHistory.loadError")}
          </Typography>
          <Button size="small" onClick={onRetryHistory}>
            {t("general.retry")}
          </Button>
        </Box>
      )}

      {historyGroups.map((group) => {
        const [firstNote] = group;
        const previousMileage =
          currentMileage != null && firstNote.mileageDelta != null
            ? currentMileage - firstNote.mileageDelta
            : undefined;
        const deltaText = mileageDeltaText(firstNote.mileageDelta, mileageUnit);
        const unitLabel = t(
          `dealer.auctionDetailsPage.mileageUnit.${mileageUnit ?? MileageUnit.KM}`
        );
        const isDecrease = (firstNote.mileageDelta ?? 0) < 0;
        const semantic = isDecrease
          ? theme.palette.semantic.error
          : theme.palette.semantic.info;

        return (
          <Box key={`note-history-group-${firstNote.auctionId}`} sx={{ mb: 2 }}>
            <Typography variant="body2" sx={sectionLabelSx}>
              {t("dealer.auctionDetailsPage.noteHistory.previousAuction")}
              {" · "}
              {getDateStringDMY(firstNote.auctionCreatedAt)}
            </Typography>

            {previousMileage != null &&
              deltaText &&
              firstNote.mileageDelta !== 0 && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: 2,
                    backgroundColor: semantic.focus12p,
                    borderRadius: 1,
                    p: 2,
                    mb: 1.5
                  }}
                >
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      {t("dealer.auctionDetailsPage.noteHistory.mileageThen")}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: theme.typography.fontWeightBold }}
                    >
                      {addThousandsSeparator(previousMileage)} {unitLabel}
                    </Typography>
                  </Box>
                  {!isPreview && (
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        {t("dealer.auctionDetailsPage.noteHistory.mileageNow")}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: theme.typography.fontWeightBold,
                          color: isDecrease ? semantic.main : "inherit"
                        }}
                      >
                        {deltaText}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

            {group.map((note) => (
              <NoteItem
                key={`dealer-note-history-${note.id}`}
                note={note}
                onDelete={() => {}}
                readOnly
              />
            ))}
          </Box>
        );
      })}

      {!isPreview && !hasNotes && !hasHistory && (
        <Typography
          variant="body2"
          sx={{ color: theme.palette.text.secondary }}
        >
          {t("dealer.auctionDetailsPage.noNotes")}
        </Typography>
      )}
    </>
  );
};

export default NotesList;
